import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  createCrmJob,
  CrmJobDTO,
  csrfTokenCall,
  ClientContactDTO,
  getUserByEmail,
  FieldTypeEnum,
  ICustomField,
  CrmJobStatusDTO,
  getCrmJobStatusList,
} from 'wisestep-api';
import {
  ControlledTextInput,
  IUserDTO,
  Label,
  WsButton,
  WsColors,
  WsLocationSearch,
  WsEditor,
  useWsEditor,
  WsSelect,
} from 'wisestep-base';

import { checkIfRequiredField } from '../../../components/BaseCustomField/CustomFieldHelper';
import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { WsTagsFilter } from '../../../components/filters/components';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { useUserContext } from '../../../context/UserContext';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import CustomField from '../../clientListingPage/components/CustomField';
import { getValidationSchema } from '../helper/crmJobHelper';

type CrmJobFormData = Omit<CrmJobDTO, 'clientContact'> & {
  clientContact?: Omit<ClientContactDTO, 'supervisor'> | null;
};

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.TertiaryShades100,
  width: '50vw',
  padding: 2,
}));

export interface StatusList {
  label: string;
  value: string;
}

interface CrmJobFormProps {
  defaultCrmJobData?: CrmJobDTO;
  handleClose: () => void;
  postSubmit?: (crmJob: CrmJobDTO) => void;
  customFields: ICustomField[];
  defaultFields: ICustomField[];
}

const CrmJobForm = ({
  defaultCrmJobData,
  postSubmit,
  handleClose,
  customFields,
  defaultFields,
}: CrmJobFormProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const editor = useWsEditor('', (htmlBody) => methods.setValue('jobDescription', htmlBody ?? ''));
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const [allJobStatusList, setAllJobStatusList] = useState<CrmJobStatusDTO[]>([]);
  const [statusList, updateStatusList] = useState<StatusList[]>([]);
  const [defaultLeadStatus, setDefaultLeadStatus] = useState<CrmJobStatusDTO>();

  const methods = useForm<CrmJobFormData>({
    resolver: yupResolver(getValidationSchema(defaultFields || [])),
    defaultValues: defaultCrmJobData || {
      title: '',
      linkedInJobUrl: '',
      jobDescription: '',
      jobFunction: '',
      clientId: '',
      client: null,
      location: null,
      jobStatus: null,
      jobClosed: false,
      jobStatusId: '',
    },
  });

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  const selectedEmail = methods.watch('ownerId') ?? '';

  const onSubmit = async (data: CrmJobFormData) => {
    try {
      setIsSaving(true);
      data.owner = getJobOwner();
      data.ownerId = getJobOwner()?.email || '';
      if (data.jobStatusId) {
        data.jobStatus = allJobStatusList.find((status) => status.id === data.jobStatusId) || null;
      }
      const resp = await createCrmJob({ ...data, isApp: true });
      if (resp?.data) {
        setNotification(success('Job created successfully'));
        methods.reset();
        postSubmit?.(resp.data);
        handleClose();
      }
    } catch (error) {
      setNotification(failed('Error creating CRM job'));
      console.error('Error creating CRM job:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getJobOwner = () =>
    methods.getValues().owner || {
      name: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
      email: loggedInUser.email || '',
    };

  useEffect(() => {
    const getUserDetails = async () => {
      const res = await getUserByEmail(selectedEmail);
      if (res?.data) {
        const firstName = res.data.firstName || '';
        const lastName = res.data.lastName || '';
        const recruiterName = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
        const owner = { name: recruiterName, email: selectedEmail };
        methods.setValue('ownerId', selectedEmail);
        methods.setValue('owner', owner);
      } else {
        methods.setValue('owner', null);
      }
    };
    selectedEmail ? getUserDetails() : methods.setValue('owner', null);
  }, [selectedEmail]);

  const getStatusList = async () => {
    const response = await getCrmJobStatusList();
    setAllJobStatusList(response?.data ?? []);
    const statusData: StatusList[] = [];
    let defaultStatusName = '';

    response.data.forEach((obj: CrmJobStatusDTO) => {
      methods.setValue('jobStatusId', obj.id);
      defaultStatusName = obj.id;
      setDefaultLeadStatus(obj);
      statusData.push({
        label: obj.statusName as string,
        value: obj.id ?? '',
      });
    });

    if (defaultCrmJobData?.jobStatus?.statusName) {
      const selectedOption = statusData.find(
        (item) => item.label === defaultCrmJobData?.jobStatus?.statusName
      );
      if (selectedOption) {
        methods.setValue('jobStatusId', selectedOption.value as string);
      }
    } else {
      methods.setValue('jobStatusId', defaultStatusName);
    }

    statusData.sort((a, b) => {
      const aLabel = a.label.trim().toLowerCase();
      const bLabel = b.label.trim().toLowerCase();
      const aIsOpen = aLabel === 'open';
      const bIsOpen = bLabel === 'open';

      return aIsOpen === bIsOpen ? aLabel.localeCompare(bLabel) : aIsOpen ? -1 : 1;
    });

    updateStatusList(statusData);
  };

  useEffect(() => {
    getStatusList();
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <Box p={2}>
            <Grid container spacing={2} pr={5}>
              <Grid item xs={12} sm={6}>
                <ControlledTextInput
                  name="title"
                  type="text"
                  fullWidth
                  labelText="Job Title"
                  variant="outlined"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'title', true)}
                  placeholder="Enter Job Title"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextInput
                  name="linkedInJobUrl"
                  type="text"
                  fullWidth
                  labelText="Job URL"
                  variant="outlined"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'linkedInJobUrl', true)}
                  placeholder="Enter URL"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WsLocationSearch
                  size="small"
                  placeHolder="Hiring Location"
                  name="location"
                  variant="outlined"
                  multiSelect={false}
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'location', true)}
                  token={token}
                  authorization={auth}
                  wisestepApi={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>
                  Client
                  {checkIfRequiredField(defaultFields, 'client', true) && '*'}
                </Label>
                <ClientAutocomplete name="client" placeHolder="Select Client" />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsTagsFilter placeHolder="Enter Tags" name="tags" label="Tags" />
              </Grid>
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="jobSource"
                  type="text"
                  fullWidth
                  labelText="Job Source"
                  variant="outlined"
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <Label>Job Owner</Label>
                <Controller
                  name="ownerId"
                  control={methods.control}
                  defaultValue={methods.getValues('owner')?.email || loggedInUser.email}
                  render={({ field }) => (
                    <RecruiterSuggestions
                      {...field}
                      value={field.value || ''}
                      defaultValue={field.value ? [field.value] : []}
                      onSelectMultipleValues={(emails: string[]) => {
                        const selectedEmail = emails[0] || '';
                        field.onChange(selectedEmail);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsSelect
                  key="status"
                  name="jobStatusId"
                  label="Status"
                  selectArray={statusList}
                  variant="outlined"
                  showMandatorySymbol
                  defaultValue={defaultLeadStatus?.statusName}
                />
              </Grid>
              {customFields &&
                customFields.map(
                  (field) =>
                    field.visible && (
                      <Grid
                        item
                        key={field.id}
                        p={1}
                        sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 6}
                      >
                        <CustomField {...field} name={`customFields.${field.name}`} />
                      </Grid>
                    )
                )}
              <Grid item>
                <Label>Description *</Label>
                <Box minHeight={210} border={1} borderColor={WsColors.BorderGrey}>
                  <WsEditor key="jobDescription" editor={editor} resizable={true} />
                </Box>
              </Grid>
            </Grid>
          </Box>
          <DrawerFooter>
            <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
              <WsButton variant="outlined" size="large" onClick={handleClose}>
                Cancel
              </WsButton>
              <WsButton variant="contained" size="large" type="submit" disabled={isSaving}>
                Save
              </WsButton>
            </Stack>
          </DrawerFooter>
        </form>
      </FormProvider>
    </>
  );
};

export default CrmJobForm;
